import React from 'react';
import clsx from 'clsx';
import { Formik } from 'formik';
import { EllipsisVertical, GripVertical, Heading2, Heading3 } from 'lucide-react';
import { ROOT_CONTAINER } from '@repo/lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { FormElement, Label } from '@dashboard/components/Form';
import { FormSubmit, TextInput } from '@dashboard/components/Formik';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import FormError from '@dashboard/components/Formik/components/FormError';
import MoveToMenu from './MoveToMenu';
import DisabledBadge from './DisabledBadge';
import { useFormLayout } from '../FormLayoutContext';

export const HEADING_LEVEL_LABELS = { heading: 'Heading', subheading: 'Subheading' };

export default function HeadingItem({
  heading,
  dragHandleProps,
  containerId = ROOT_CONTAINER,
  onClose,
  disabled,
}) {
  const { updateHeading, removeHeading, duplicateBlock, setBlockDisabled } = useFormLayout();
  const value = `heading:${heading.id}`;
  const Icon = heading.level === 'subheading' ? Heading3 : Heading2;

  return (
    <AccordionItem value={value} className="border-b-0">
      <div className="flex flex-row items-center gap-2 px-1">
        <div
          {...dragHandleProps}
          className={clsx(
            buttonVariants({ variant: 'ghost', size: 'icon' }),
            'cursor-grab active:cursor-grabbing',
          )}
        >
          <GripVertical className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <AccordionTrigger className="py-3 min-w-0 items-center hover:no-underline">
            <div className="flex min-w-0 gap-x-2 items-center">
              <Icon className="size-4 shrink-0" aria-hidden="true" />
              <span className="sr-only">{HEADING_LEVEL_LABELS[heading.level]}:</span>
              <div className={clsx('truncate min-w-0', { 'opacity-60': heading.disabled })}>
                {heading.text}
                {heading.disabled && <DisabledBadge />}
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            <ShadcnButton variant="ghost" size="icon" aria-label="More options" disabled={disabled}>
              <EllipsisVertical className="size-4" />
            </ShadcnButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <MoveToMenu itemKey={value} currentContainerId={containerId} />
            <DropdownMenuItem onSelect={() => duplicateBlock(value)}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setBlockDisabled(value, !heading.disabled)}>
              {heading.disabled ? 'Enable' : 'Disable'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => removeHeading(heading.id)}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <AccordionContent className={clsx('pb-4 pt-2', { 'px-3': containerId !== ROOT_CONTAINER })}>
        <Formik
          enableReinitialize
          initialValues={{ text: heading.text, level: heading.level }}
          validateOnBlur={false}
          validate={values => (values.text.trim() ? {} : { text: 'Add the heading text' })}
          onSubmit={async (values, { setSubmitting }) => {
            await updateHeading(heading.id, { text: values.text.trim(), level: values.level });
            setSubmitting(false);
          }}
        >
          {({ handleSubmit, dirty, isSubmitting, resetForm, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <FormElement>
                <Label for={`${value}_text`}>Text *</Label>
                <TextInput
                  id={`${value}_text`}
                  name="text"
                  type="text"
                  placeholder="Heading"
                  clearable
                />
                <FormError name="text" />
              </FormElement>
              <FormElement>
                <Label id={`${value}_style`}>Style</Label>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  aria-labelledby={`${value}_style`}
                  value={values.level}
                  onValueChange={level => level && setFieldValue('level', level)}
                  className="justify-start"
                >
                  {Object.entries(HEADING_LEVEL_LABELS).map(([level, label]) => (
                    <ToggleGroupItem key={level} value={level} className="px-2.5">
                      {label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormElement>
              <FormSubmit align="end">
                <FormSubmit.State visible={!!dirty} />
                <FormSubmit.Cancel
                  onClick={() => {
                    resetForm();
                    onClose?.();
                  }}
                >
                  Cancel
                </FormSubmit.Cancel>
                <FormSubmit.Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                  Save
                </FormSubmit.Button>
              </FormSubmit>
            </form>
          )}
        </Formik>
      </AccordionContent>
    </AccordionItem>
  );
}
